import { Bell, User, GraduationCap, DollarSign, Percent, Check, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { Button } from '@pps/ui/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@pps/ui/components/ui/popover';
import { ScrollArea } from '@pps/ui/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import i18n from '@/i18n';

const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  new_lead: User,
  new_student: GraduationCap,
  plan_closed: DollarSign,
  discount_pending: Percent,
  new_member: UserCheck,
};

const TYPE_ROUTES: Record<string, string> = {
  new_member: '/membros',
  new_lead: '/leads',
  new_student: '/alunos',
};

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const locale = i18n.language === 'pt' ? ptBR : enUS;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] bg-destructive text-destructive-foreground border-0 flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="text-sm font-semibold">{t('notifications.title')}</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 gap-1 text-muted-foreground"
              onClick={() => markAllAsRead.mutate()}
            >
              <Check className="h-3 w-3" />
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t('notifications.noNotifications')}</p>
          ) : (
            <div className="divide-y">
          {notifications.map(n => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                    onClick={() => {
                      markAsRead.mutate(n.id);
                      const route = TYPE_ROUTES[n.type];
                      if (route) navigate(route);
                    }}
                  >
                    <div className="rounded-full bg-primary/10 p-2 shrink-0">
                      <Icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{n.title}</p>
                      {n.body && <p className="text-xs text-muted-foreground truncate">{n.body}</p>}
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale })}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
        <div className="border-t px-4 py-2">
          <a href="/notificacoes" className="text-xs text-primary hover:underline">{t('notifications.viewAll')}</a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
